import type { QuizAnswers, VesselQuestions } from '@/types/quizTypes.ts';

import {
  CRUISING_LOCATION_OPTIONS,
  HULL_MATERIAL_OPTIONS,
  USAGE_TYPE_OPTIONS,
  VESSEL_TYPE_OPTIONS,
} from './QuizModal/quizModal.data.ts';

/**
 * Field types the /api/ai/vessel/questionnaire/autofill endpoint accepts. This schema is sent to
 * that service as-is, so anything outside this union comes back as a 422 — the QuizModal's own
 * render types (which include 'country') are a separate, frontend-only concern.
 */
type AutofillQuestionType = 'number' | 'single_select' | 'multi_select' | 'text';

export const VESSEL_QUESTIONNAIRE_SCHEMA = [
  {
    id: 'registration_number',
    label: 'Registration Number',
    type: 'text',
  },
  {
    id: 'flag_state',
    label: 'Flag State / Registration',
    type: 'text',
    unit: 'ISO 3166-1 alpha-2 code',
  },
  {
    id: 'make',
    label: 'Make',
    type: 'text',
  },
  {
    id: 'model',
    label: 'Model',
    type: 'text',
  },
  {
    id: 'year_built',
    label: 'Year Built',
    type: 'number',
    unit: 'year',
  },
  {
    id: 'length_overall',
    label: 'Length Overall',
    type: 'number',
    unit: 'ft',
  },
  {
    id: 'draft_m',
    label: 'Draft',
    type: 'number',
    unit: 'm',
  },
  {
    id: 'beam_m',
    label: 'Beam',
    type: 'number',
    unit: 'm',
  },
  {
    id: 'guest_cabins',
    label: 'Number of Guest Cabins',
    type: 'number',
    unit: 'pcs',
  },
  {
    id: 'crew_count',
    label: 'Number of Crew',
    type: 'number',
    unit: 'persons',
  },
  {
    id: 'country_built',
    label: 'Country Vessel Built In',
    type: 'text',
    unit: 'ISO 3166-1 alpha-2 code',
  },
  {
    id: 'home_port',
    label: 'Home Port',
    type: 'text',
  },
  {
    id: 'cruising_areas',
    label: 'Cruising Locations',
    type: 'multi_select',
    options: CRUISING_LOCATION_OPTIONS,
  },
  {
    id: 'usage_type',
    label: 'Vessel Use',
    type: 'single_select',
    options: USAGE_TYPE_OPTIONS,
  },
  {
    id: 'vessel_type',
    label: 'Vessel Type',
    type: 'multi_select',
    options: VESSEL_TYPE_OPTIONS,
  },
  {
    id: 'hull_material',
    label: 'Hull Material',
    type: 'multi_select',
    options: HULL_MATERIAL_OPTIONS,
  },
  {
    id: 'estimated_value',
    label: 'Estimated Value',
    type: 'number',
  },
  {
    id: 'engines',
    label: 'Engines',
    type: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    type: 'text',
  },
] as const satisfies readonly {
  id: string;
  label: string;
  type: AutofillQuestionType;
  unit?: string;
  options?: string[];
}[];

export type VesselQuestionnaireId = (typeof VESSEL_QUESTIONNAIRE_SCHEMA)[number]['id'];

export type VesselQuestionnaireAutofillResponse = {
  suggested_answers: Partial<QuizAnswers>;
  filled_ids: VesselQuestionnaireId[];
  still_unanswered: VesselQuestionnaireId[];
};

export const isEmptyAnswer = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const isVesselQuestionnaireComplete = (
  answers?: Partial<Record<VesselQuestionnaireId, unknown>>,
) => VESSEL_QUESTIONNAIRE_SCHEMA.every(({ id }) => !isEmptyAnswer(answers?.[id]));

export const getVesselQuestionnaireAnswers = (
  questions?: VesselQuestions | QuizAnswers,
): QuizAnswers => {
  if (questions && 'answers' in questions) {
    return questions.answers;
  }

  return questions ?? {};
};

/** Suggestions fill only what is empty: a value already given is never overwritten. */
export const mergeVesselQuestionnaireAnswers = <T extends object>(
  answers: T | undefined,
  suggestions: Partial<NoInfer<T>>,
): T => {
  const merged = { ...answers } as T;

  Object.entries(suggestions).forEach(([key, value]) => {
    if (isEmptyAnswer(merged[key as keyof T]) && !isEmptyAnswer(value)) {
      Object.assign(merged, { [key]: value });
    }
  });

  return merged;
};

export const getAppliedSuggestionIds = (
  answers: QuizAnswers | undefined,
  suggestions: Partial<QuizAnswers>,
) =>
  Object.keys(suggestions).filter((key) => {
    const id = key as keyof QuizAnswers;
    return isEmptyAnswer(answers?.[id]) && !isEmptyAnswer(suggestions[id]);
  }) as VesselQuestionnaireId[];

export const getQuestionLabels = (ids: VesselQuestionnaireId[]) =>
  VESSEL_QUESTIONNAIRE_SCHEMA.filter(({ id }) => ids.includes(id)).map(({ label }) => label);
